import React, { useLayoutEffect, useRef, useState } from "react";
import { Flex, Popover, theme } from "antd";
import { AffiliationIcon } from "@/components/xcons/form/filter/people/AffiliationIcon";
import {
  PEOPLE_FILTER_GREEN,
  PEOPLE_FILTER_GREEN_BG,
} from "@/components/xcons/form/filter/people/PeopleFilterShell";
import type { CargoDeOperador } from "./types";

/** La clave de un cargo: `store:<código>` o `seller:<código>`. */
export const claveDeCargo = (cargo: CargoDeOperador) =>
  cargo.seller ? `seller:${cargo.seller}` : `store:${cargo.store}`;

const etiqueta = (cargo: CargoDeOperador) =>
  cargo.name?.trim() || cargo.seller || cargo.store || "";

/**
 * Los stores y sellers de los que es operador un candidato, como tags con el ícono y el
 * nombre —el mismo chip que el filtro de Operadores del listado de Contactos—. El tipo lo
 * dice el ícono, así que no se escribe "Store Operador" (xcons-docs#383).
 *
 * **Clickeables, y el elegido queda en verde.** Sirve para quien está dado de alta en
 * varios lugares: se elige por cuál se lo está sumando. Es una elección de pantalla: el
 * permiso del Catálogo y la participación en un producto son **globales** por persona
 * (spec `permisos-identidad`), así que el tag no cambia lo que se guarda.
 *
 * **Se muestran todos, sin resumir en "+N"** (xcons-docs#409): uno escondido en un tooltip
 * no se puede clickear, y quien tiene muchos cargos es justo quien más necesita elegir.
 *
 * La usan los dos buscadores del Catálogo: Permisos y Participantes del producto.
 *
 * **Como `lista`, sólo informa** (xcons-docs#466): ícono y nombre en gris, sin recuadro.
 * Es la de Participantes del producto, donde a la persona se la suma sin importar desde
 * dónde, y un tag que parece botón invita a elegir algo que no existe.
 */
export const CargosDeOperador: React.FC<{
  permissions?: CargoDeOperador[];
  /** La clave del tag elegido (`claveDeCargo`). */
  seleccionado?: string | null;
  onSeleccionar?: (cargo: CargoDeOperador) => void;
  lista?: boolean;
}> = ({ permissions, seleccionado, onSeleccionar, lista }) => {
  const cargos = permissions ?? [];
  if (!cargos.length) return null;

  if (lista) {
    return (
      <Flex wrap gap="2px 12px" style={{ marginTop: 4 }}>
        {cargos.map((cargo) => (
          <ItemDeLista key={claveDeCargo(cargo)} cargo={cargo} />
        ))}
      </Flex>
    );
  }

  return (
    <Flex wrap gap={6} style={{ marginTop: 6 }}>
      {cargos.map((cargo) => {
        const clave = claveDeCargo(cargo);
        const activo = clave === seleccionado;
        const color = activo ? PEOPLE_FILTER_GREEN : "rgba(0,0,0,0.65)";
        return (
          <span
            key={clave}
            role={onSeleccionar ? "radio" : undefined}
            aria-checked={onSeleccionar ? activo : undefined}
            // Sin `stopPropagation`: dentro del desplegable, el click tiene que llegar
            // también a la opción, que es la que elige a la persona.
            onClick={() => onSeleccionar?.(cargo)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 8px",
              borderRadius: 4,
              fontSize: 12,
              lineHeight: "18px",
              cursor: onSeleccionar ? "pointer" : "default",
              userSelect: "none",
              color,
              fontWeight: activo ? 600 : 400,
              border: `1px solid ${activo ? PEOPLE_FILTER_GREEN : "#d9d9d9"}`,
              background: activo ? PEOPLE_FILTER_GREEN_BG : "#fff",
            }}
          >
            <AffiliationIcon
              type={cargo.seller ? "seller" : "store"}
              color={color}
            />
            {etiqueta(cargo)}
          </span>
        );
      })}
    </Flex>
  );
};

const GRIS = "rgba(0,0,0,0.45)";
/** El espacio entre un lugar y el siguiente en la lista. */
const SEPARACION = 12;

/** Un store o seller de la lista que sólo informa: ícono y nombre en gris. */
const ItemDeLista: React.FC<{ cargo: CargoDeOperador }> = ({ cargo }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 12,
      lineHeight: "18px",
      color: GRIS,
      whiteSpace: "nowrap",
    }}
  >
    <AffiliationIcon type={cargo.seller ? "seller" : "store"} color={GRIS} />
    {etiqueta(cargo)}
  </span>
);

/**
 * La lista que sólo informa, **en una sola línea** (xcons-docs#547): los lugares que entran
 * en el ancho, y el resto resumido en un "+N" en el verde primario que, con un click, abre
 * la lista completa. Es la del panel de Permisos del Catálogo, donde la fila de cada persona
 * tiene que medir lo mismo aunque opere en muchos lugares. Ahí no hay nada que elegir, así
 * que lo de xcons-docs#409 —no esconder un tag clickeable— no aplica.
 *
 * Cuántos entran se mide con una copia invisible de la lista, y se vuelve a medir cuando
 * cambia el ancho del panel.
 */
export const CargosEnUnaLinea: React.FC<{
  permissions?: CargoDeOperador[];
}> = ({ permissions }) => {
  const cargos = permissions ?? [];
  const { token } = theme.useToken();
  const contenedor = useRef<HTMLDivElement>(null);
  const copia = useRef<HTMLDivElement>(null);
  const [entran, setEntran] = useState(cargos.length);

  useLayoutEffect(() => {
    const cont = contenedor.current;
    const med = copia.current;
    if (!cont || !med) return;
    const medir = () => {
      const items = Array.from(med.children) as HTMLElement[];
      // El último de la copia es un "+99" de muestra: lo que ocupa el resumen.
      const resumen = (items.pop()?.offsetWidth ?? 0) + SEPARACION;
      const ancho = cont.clientWidth;
      let usado = 0;
      let n = 0;
      for (let i = 0; i < items.length; i++) {
        const w = items[i].offsetWidth + (i ? SEPARACION : 0);
        const quedan = i < items.length - 1;
        if (usado + w + (quedan ? resumen : 0) > ancho) break;
        usado += w;
        n++;
      }
      setEntran(n);
    };
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(cont);
    return () => obs.disconnect();
  }, [cargos.length]);

  if (!cargos.length) return null;
  const resto = cargos.length - entran;

  // `overflow: hidden` recorta la copia invisible, que es más ancha que la fila: sin eso
  // estira lo que la contiene y aparece un scroll horizontal.
  return (
    <div
      ref={contenedor}
      style={{ position: "relative", marginTop: 4, overflow: "hidden" }}
    >
      <div
        ref={copia}
        aria-hidden
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          display: "flex",
          gap: SEPARACION,
          whiteSpace: "nowrap",
        }}
      >
        {cargos.map((cargo) => (
          <ItemDeLista key={claveDeCargo(cargo)} cargo={cargo} />
        ))}
        <span style={{ fontSize: 12, fontWeight: 600 }}>+99</span>
      </div>
      <Flex gap={SEPARACION} align="center" style={{ overflow: "hidden" }}>
        {cargos.slice(0, entran).map((cargo) => (
          <ItemDeLista key={claveDeCargo(cargo)} cargo={cargo} />
        ))}
        {resto > 0 && (
          <Popover
            trigger="click"
            placement="bottomLeft"
            content={
              <Flex vertical gap={4}>
                {cargos.map((cargo) => (
                  <ItemDeLista key={claveDeCargo(cargo)} cargo={cargo} />
                ))}
              </Flex>
            }
          >
            <button
              type="button"
              aria-label={`Ver los ${cargos.length} stores y sellers`}
              style={{
                border: 0,
                background: "none",
                padding: 0,
                cursor: "pointer",
                fontSize: 12,
                lineHeight: "18px",
                fontWeight: 600,
                color: token.colorPrimary,
                whiteSpace: "nowrap",
              }}
            >
              +{resto}
            </button>
          </Popover>
        )}
      </Flex>
    </div>
  );
};
